import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppUser } from '../../../hooks/useAppUser';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';
import StripeProvider from '../../../components/common/StripeProvider';
import PaymentMethodForm from '../components/PaymentMethodForm';
import CreditPurchaseDialog from '../components/CreditPurchaseDialog';
import AutoRechargeDialog from '../components/AutoRechargeDialog';

function BillingSettings() {
  const queryClient = useQueryClient();
  const api = useApi();
  const { user } = useAppUser();
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [autoRechargeDialogOpen, setAutoRechargeDialogOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);

  // Include user ID in query keys so cache is per-user
  const userId = user?.id || user?._id || user?.email;

  const { data: billingData, isLoading, error: queryError } = useQuery({
    queryKey: ['billing', userId],
    queryFn: async () => {
      const [subscriptionRes, paymentMethodsRes, invoicesRes] = await Promise.all([
        api.get('/api/billing/subscription'),
        api.get('/api/billing/payment-methods'),
        api.get('/api/billing/invoices'),
      ]);
      return {
        subscription: subscriptionRes,
        paymentMethods: paymentMethodsRes?.paymentMethods || [],
        invoices: invoicesRes?.invoices || [],
      };
    },
    enabled: !!userId,
    retry: false, // Don't retry on mode mismatch errors
  });

  // Get setup intent for adding a new card
  const setupIntentMutation = useMutation({
    mutationFn: () => api.get('/api/billing/setup-intent'),
    onSuccess: (data) => {
      setClientSecret(data.clientSecret);
      setAddCardOpen(true);
    },
    onError: (err) => {
      setError(err.message || 'Failed to initialize card setup');
    },
  });

  // Remove payment method
  const removeCardMutation = useMutation({
    mutationFn: (paymentMethodId) => api.delete(`/api/billing/payment-methods/${paymentMethodId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['billing']);
    },
    onError: (err) => {
      setError(err.message || 'Failed to remove payment method');
    },
  });

  // Set default payment method
  const setDefaultMutation = useMutation({
    mutationFn: (paymentMethodId) => api.post(`/api/billing/payment-methods/${paymentMethodId}/default`),
    onSuccess: () => {
      queryClient.invalidateQueries(['billing']);
    },
    onError: (err) => {
      setError(err.message || 'Failed to set default payment method');
    },
  });

  const handleAddCard = () => {
    setError(null);
    setupIntentMutation.mutate();
  };

  const handleCardAdded = () => {
    setAddCardOpen(false);
    setClientSecret(null);
    queryClient.invalidateQueries(['billing']);
  };

  const handleRemoveCard = (paymentMethodId) => {
    if (window.confirm('Are you sure you want to remove this payment method?')) {
      removeCardMutation.mutate(paymentMethodId);
    }
  };

  const handleSetDefault = (paymentMethodId) => {
    setDefaultMutation.mutate(paymentMethodId);
  };

  const handlePurchaseClose = (result) => {
    setPurchaseDialogOpen(false);
    if (result?.success) {
      queryClient.invalidateQueries(['billing']);
    }
  };

  const handleAutoRechargeClose = (saved) => {
    setAutoRechargeDialogOpen(false);
    if (saved) {
      queryClient.invalidateQueries(['billing']);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (queryError) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Billing
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {queryError.message || 'Failed to load billing information'}
        </Alert>
      </Box>
    );
  }

  const { subscription, paymentMethods, invoices } = billingData || {};
  const hasPaymentMethod = paymentMethods?.length > 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Billing
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          mb: 4
        }}>
        Manage your subscription, payment methods, and view invoices.
      </Typography>
      {/* Current Plan */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Plan
        </Typography>
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {subscription?.planName || 'Pay As You Go'}
                </Typography>
                <Typography variant="body2" gutterBottom sx={{
                  color: "text.secondary"
                }}>
                  {subscription?.status === 'active' ? 'Active' : 'No active subscription'}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "primary.main",
                    mt: 2
                  }}>
                  ${(subscription?.credits ?? 0).toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Current credit balance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Box>
              <Typography variant="subtitle2" sx={{
                color: "text.secondary"
              }}>
                Auto-recharge
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AutorenewIcon
                  fontSize="small"
                  color={subscription?.autoRecharge?.enabled ? 'success' : 'disabled'}
                />
                <Typography variant="body1">
                  {subscription?.autoRecharge?.enabled
                    ? `Enabled - Recharge $${subscription.autoRecharge.amount} when below $${subscription.autoRecharge.threshold}`
                    : 'Disabled'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setAutoRechargeDialogOpen(true)}
              >
                Configure Auto-recharge
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => setPurchaseDialogOpen(true)}
            disabled={!hasPaymentMethod}
          >
            Add Credits
          </Button>
          <Button variant="outlined" href="/usage" component="a">
            View Usage Details
          </Button>
        </Box>
        {!hasPaymentMethod && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1
            }}>
            Add a payment method to purchase credits.
          </Typography>
        )}
      </Paper>
      {/* Payment Methods */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Payment Methods</Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddCard}
            disabled={setupIntentMutation.isPending}
          >
            {setupIntentMutation.isPending ? 'Loading...' : 'Add Card'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {paymentMethods?.length === 0 ? (
          <Alert severity="info">
            No payment methods on file. Add a card to purchase credits.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paymentMethods?.map((method) => (
              <Card key={method.id} variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CreditCardIcon />
                    <Box>
                      <Typography variant="body1">
                        {method.brand?.toUpperCase() || 'Card'} •••• {method.last4}
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: "text.secondary"
                      }}>
                        Expires {method.expMonth}/{method.expYear}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {method.isDefault ? (
                      <Chip label="Default" color="primary" size="small" />
                    ) : (
                      <Button
                        size="small"
                        startIcon={<StarIcon />}
                        onClick={() => handleSetDefault(method.id)}
                        disabled={setDefaultMutation.isPending}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCard(method.id)}
                      disabled={removeCardMutation.isPending || method.isDefault}
                    >
                      Remove
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>
      {/* Invoice History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Invoice History
        </Typography>

        {invoices?.length === 0 ? (
          <Typography sx={{
            color: "text.secondary"
          }}>No invoices yet</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices?.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    hover
                    onDoubleClick={() => {
                      const url = invoice.hostedInvoiceUrl || invoice.pdfUrl;
                      if (url) window.open(url, '_blank');
                    }}
                    sx={{ cursor: invoice.hostedInvoiceUrl || invoice.pdfUrl ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      {dayjs(invoice.created).format('MMM D, YYYY')}
                    </TableCell>
                    <TableCell>
                      {invoice.description || `Invoice ${invoice.number || invoice.id.slice(-8)}`}
                    </TableCell>
                    <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status}
                        color={invoice.status === 'paid' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* Add Card Dialog */}
      <Dialog
        open={addCardOpen}
        onClose={() => setAddCardOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: { bgcolor: 'background.paper' },
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Payment Method
          <IconButton onClick={() => setAddCardOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {clientSecret && (
            <StripeProvider clientSecret={clientSecret}>
              <PaymentMethodForm
                clientSecret={clientSecret}
                onSuccess={handleCardAdded}
                onError={(err) => setError(err.message)}
                submitLabel="Add Card"
              />
            </StripeProvider>
          )}
        </DialogContent>
      </Dialog>
      {/* Credit Purchase Dialog */}
      <CreditPurchaseDialog
        open={purchaseDialogOpen}
        onClose={handlePurchaseClose}
        currentCredits={subscription?.credits || 0}
        paymentMethods={paymentMethods || []}
      />
      {/* Auto-Recharge Dialog */}
      <AutoRechargeDialog
        open={autoRechargeDialogOpen}
        onClose={handleAutoRechargeClose}
        currentConfig={subscription?.autoRecharge || {}}
        hasPaymentMethod={hasPaymentMethod}
      />
    </Box>
  );
}

export default BillingSettings;
